import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function SubscribeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const [tier, setTier] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setTier((data.metadata && data.metadata.tier) || "");
          setStatus("paid");
          try { await refreshUser(); } catch {}
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setAttempts((n) => n + 1);
        if (attempts < 24) setTimeout(poll, 2500);
      } catch (e) {
        if (!cancelled) {
          setAttempts((n) => n + 1);
          if (attempts < 24) setTimeout(poll, 2500);
          else setStatus("failed");
        }
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-[760px] mx-auto px-6 lg:px-10 pt-32 pb-24 text-center" data-testid="subscribe-success-page">
      {status === "polling" && (
        <div data-testid="subscribe-status-polling">
          <Loader2 size={42} className="animate-spin mx-auto mb-8" style={{ color: "var(--brass)" }} />
          <p className="eyebrow mb-4">Settling Payment</p>
          <h1 className="font-display text-5xl md:text-6xl leading-tight mb-6">
            Confirming your <em style={{ color: "var(--brass)" }}>membership</em>…
          </h1>
          <p className="lede">
            Stripe is settling the transaction. This usually takes 3–10 seconds. Please don't close this window.
          </p>
        </div>
      )}

      {status === "paid" && (
        <div data-testid="subscribe-status-paid">
          <CheckCircle2 size={48} className="mx-auto mb-8" style={{ color: "var(--brass)" }} />
          <p className="eyebrow mb-4">Membership Active</p>
          <h1 className="font-display text-5xl md:text-6xl leading-tight mb-6">
            Welcome to <em style={{ color: "var(--brass)" }}>{tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : "the archive"}</em>.
          </h1>
          <p className="lede mb-10">
            Your WealthFirst membership is active. The full editorial archive, member-only interviews, and dispatches are unlocked.
            A receipt has been emailed to you.
          </p>
          <Link
            to="/episodes"
            className="inline-flex items-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.18em]"
            style={{ background: "var(--brass)", color: "var(--bg)" }}
            data-testid="goto-episodes"
          >
            Open the Archive →
          </Link>
        </div>
      )}

      {status === "failed" && (
        <div data-testid="subscribe-status-failed">
          <p className="eyebrow mb-4" style={{ color: "#E08585" }}>Settlement Issue</p>
          <h1 className="font-display text-5xl md:text-6xl leading-tight mb-6">
            We couldn't confirm your payment.
          </h1>
          <p className="lede mb-10">
            This usually means the session expired or Stripe is taking longer than expected. If your card was charged,
            email <a href="mailto:support@wealthfirstinc.tv" className="brass-link">support@wealthfirstinc.tv</a>{" "}
            with your transaction reference and we'll activate your membership manually.
          </p>
          <Link to="/subscribe" className="brass-link">← Back to Membership</Link>
        </div>
      )}
    </div>
  );
}
